"use client";

import React from "react";
import { ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  PostsProvider,
  usePosts,
} from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


declare const wvcClient: any;

function PostSkeleton() {
  return (
    <div className="mx-auto max-w-3xl animate-pulse">
      <div className="mb-8 h-8 w-24 rounded-md bg-muted"></div>
      <div className="mb-6 h-12 w-3/4 rounded-md bg-muted"></div>
      <div className="mb-10 flex gap-4">
        <div className="size-8 rounded-lg bg-muted"></div>
        <div className="h-8 w-32 rounded-md bg-muted"></div>
      </div>
      <div className="mb-12 aspect-video w-full rounded-2xl bg-muted"></div>
      <div className="space-y-4">
        <div className="h-4 w-full rounded-md bg-muted"></div>
        <div className="h-4 w-full rounded-md bg-muted"></div>
        <div className="h-4 w-5/6 rounded-md bg-muted"></div>
        <div className="h-4 w-4/6 rounded-md bg-muted"></div>
      </div>
    </div>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 rounded-xl bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card
          key={post.id}
          data-index={i}
          className="flex h-full flex-col overflow-hidden border-border/50 transition-shadow hover:shadow-lg bg-card"
        >
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="size-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="flex-grow p-5">
            {post.categories && post.categories.length > 0 && (
              <span className="mb-2 text-xs font-semibold tracking-widest text-primary uppercase">
                {post.categories[0].name}
              </span>
            )}
            <CardTitle className="mb-2 font-default text-lg font-bold line-clamp-2 text-card-foreground">
              <Link
                to={post.link || "#"}
                className="transition-colors hover:text-primary"
              >
                {post.title}
              </Link>
            </CardTitle>
            <CardDescription className="text-sm line-clamp-3 text-muted-foreground">
              <div
                dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
              />
            </CardDescription>
          </CardHeader>
          <CardFooter className="mt-auto p-5 pt-0">
            <Link
              to={post.link || "#"}
              className="flex items-center gap-1 text-sm font-bold text-primary hover:underline"
            >
              Read Moving Guide <ArrowRight className="size-4" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPost }: { currentPost: any }) {
  
  const categoryIds = currentPost.categories?.map((c: any) => c.id) || [];
  const queryParams: any = {
    post_type: "post",
    per_page: 3,
    exclude: [currentPost.id],
  };

  if (categoryIds.length > 0) {
    queryParams.categories = categoryIds;
  }

  const wp_query = new WP_Query(queryParams);

  return (
    <section className="mt-20">
      <h2 className="mb-8 font-default text-2xl font-bold text-foreground tracking-tight">
        More Moving Resources
      </h2>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </section>
  );
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) return <PostSkeleton />;
  
  if (error) {
    return (
      <div className="py-20 text-center text-muted-foreground font-default">
        Error loading relocation insights.
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="py-20 text-center text-muted-foreground font-default">
        Article not found.
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-3xl">
      <div className="mb-8">
        <Link to="/ResourcesPage">
          <Button
            variant="ghost"
            className="pl-0 text-muted-foreground hover:bg-transparent hover:text-primary font-default"
          >
            <ArrowLeft className="mr-2 size-4" /> Back to Resources
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-10 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-4 flex justify-center gap-2">
            {post.categories.map((cat: any, i: number) => (
              <span
                key={cat.id}
                data-index={i}
                className="rounded-md bg-primary/10 px-3 py-1 text-xs font-bold text-primary uppercase tracking-wider"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}
        <h1 className="mb-6 font-default text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl tracking-tighter">
          {post.title}
        </h1>
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground font-default">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8 border border-border">
                <AvatarImage src={post.author.avatar?.["48"]} />
                <AvatarFallback>
                  <User className="size-4" />
                </AvatarFallback>
              </Avatar>
              <span className="font-semibold text-foreground">
                Shift Expert
              </span>
            </div>
          )}
          {post.author && post.date && (
            <Separator orientation="vertical" className="h-4 bg-border" />
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-xl shadow-xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover"
          />
        </div>
      )}

      {}
      <div
        className="prose prose-lg dark:prose-invert mb-16 max-w-none font-default leading-relaxed text-foreground/90 [&>a]:text-primary [&>a]:underline [&>a]:font-semibold [&>h2]:mb-4 [&>h2]:mt-8 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:tracking-tight [&>h3]:mb-3 [&>h3]:mt-6 [&>h3]:text-2xl [&>h3]:font-bold [&>ol]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>p]:mb-6 [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12 bg-border" />

      {}
      {post.author && (
        <div className="mb-16 flex flex-col items-center gap-6 rounded-xl bg-muted/50 p-8 sm:flex-row sm:items-start border border-border/50">
          <Avatar className="size-24 border-4 border-background shadow-md">
            <AvatarImage src={post.author.avatar?.["96"]} />
            <AvatarFallback className="text-2xl font-bold bg-primary text-primary-foreground">
              S
            </AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left">
            <h3 className="mb-2 font-default text-xl font-bold text-foreground">
              About Shift Expert
            </h3>
            <p className="text-muted-foreground font-default">
              Logistics expert and relocation specialist at SHIFT. Committed to providing 
              families and businesses with the knowledge they need for a seamless move.
            </p>
          </div>
        </div>
      )}

      {}
      <RelatedPosts currentPost={post} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams =
    typeof window !== "undefined" && typeof wvcClient !== "undefined"
      ? wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
      
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="225597"
      id="single-post-template-section"
      className="bg-background py-12 md:py-24 text-foreground"
    >
      <div className="container mx-auto px-4">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}